<template>
  <v-dialog :value="value" max-width="1000" scrollable @input="$emit('input', $event)">
    <v-card v-if="value && keyName">
      <v-card-title class="d-flex align-center flex-wrap" style="gap: 8px">
        <span class="mono">{{ keyName }}</span>
        <v-spacer />
        <!-- Décider après avoir vu ses valeurs -->
        <slot name="actions" />
        <v-btn icon small :aria-label="$t('close')" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <div v-if="!data" class="text-center my-6"><v-progress-circular indeterminate color="primary" /></div>
        <template v-else>
          <div class="text-caption text--secondary mb-2">{{ $t('charging-point-fleet-configuration.scope', {read: data.read, total: data.chargingPoints}) }}</div>
          <v-alert v-if="data.sensitive" type="info" text dense>{{ $t('charging-point-fleet-configuration.sensitive') }}</v-alert>
          <div v-if="boolean" class="text-caption text--secondary mb-1">{{ $t('charging-point-tracked-keys.boolean-hint') }}</div>
          <v-text-field v-if="data.values.length > 6" v-model="valueSearch" :label="$t('charging-point-tracked-keys.value-search')" prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable class="mb-2" style="max-width: 360px" />

          <!-- Combien de bornes à chaque valeur ; un clic liste les bornes -->
          <v-simple-table dense class="mb-4">
            <tbody>
              <tr v-if="!shownRows.length"><td colspan="3" class="text--secondary">{{ $t('charging-point-tracked-keys.none-found') }}</td></tr>
              <tr v-for="row in shownRows" :key="row.id" :class="{selected: selected && selected.id === row.id}" class="clickable" @click="select(row)">
                <td class="mono" style="width: 40%">
                  <span v-if="row.missing" class="text--secondary">{{ $t('charging-point-fleet-configuration.missing') }}</span>
                  <span v-else-if="row.value === null" class="text--secondary">{{ data.sensitive ? $t('charging-point-fleet-configuration.hidden') : '∅' }}</span>
                  <template v-else-if="row.values">
                    <span>{{ $t('charging-point-tracked-keys.truth.' + row.value) }}</span>
                    <div class="d-flex flex-wrap mt-1" style="gap: 4px">
                      <v-chip v-for="spelling in row.spellings" :key="spelling.value" x-small outlined :input-value="selected && selected.id === spelling.id" @click.stop="select(spelling)">
                        {{ spelling.value }} · {{ spelling.chargingPoints }}
                      </v-chip>
                    </div>
                  </template>
                  <span v-else>{{ row.value === '' ? '""' : row.value }}</span>
                </td>
                <td><v-progress-linear :value="data.read ? row.chargingPoints * 100 / data.read : 0" height="8" rounded :color="row.missing ? 'grey' : 'primary'" /></td>
                <td class="text-right text-no-wrap" style="width: 1%">{{ $tc('charging-point-fleet-configuration.charging-points', row.chargingPoints, {count: row.chargingPoints}) }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <template v-if="selected">
            <div class="text-subtitle-2 mb-1">{{ $tc('charging-point-fleet-configuration.charging-points', list ? list.total : 0, {count: list ? list.total : 0}) }}</div>
            <v-data-table
                :headers="headers"
                :items="list ? list.rows : []"
                :loading="loadingList"
                :server-items-length="list ? list.total : 0"
                :options.sync="listOptions"
                :footer-props="{'items-per-page-options': [50, 100, 200]}"
                dense
            >
              <template #item.name="{item}">
                <router-link :to="{name: 'charging-points.detail.manage', params: {id: item.chargingPointId}}">{{ item.name || item.identifier || item.chargingPointId }}</router-link>
                <v-icon x-small :color="item.connected ? 'success' : 'grey'" class="ml-1">mdi-circle</v-icon>
              </template>
              <template #item.type="{item}">{{ [item.manufacturerName, item.productLineName].filter(v => v).join(' · ') }}</template>
              <template #item.since="{item}">{{ item.since ? formatDate(item.since, 'DD/MM/YYYY') : '' }}</template>
            </v-data-table>
          </template>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import ChargingPointConfigurationRepository from "@repository/ChargingPointConfigurationRepository";
import ChargingPointConfigurationValue from "./ChargingPointConfigurationValue";

/**
 * Une clé telle que les bornes la nomment, pas encore suivie : la répartition de ses valeurs sur le périmètre, et les
 * bornes d'une valeur. De quoi l'analyser avant de la suivre, d'en faire une information ou de la rattacher.
 */
export default {
  props: {
    value: {type: Boolean, default: false},
    keyName: {type: String, default: null},
    filters: {type: Object, default: () => ({})},
  },

  data() {
    return {
      data: null,
      valueSearch: '',
      selected: null,
      list: null,
      loadingList: false,
      listOptions: {page: 1, itemsPerPage: 50},
    }
  },

  computed: {
    // Toutes ses valeurs sont des booléens : regroupées par sens, chaque écriture dessous.
    boolean() {
      return !this.data.sensitive && ChargingPointConfigurationValue.isBoolean(this.data.values.map(row => row.value))
    },
    rows() {
      let rows = this.data.values.map((row, index) => ({id: 'v' + index, value: row.value, chargingPoints: row.chargingPoints, missing: false}))
      if (this.boolean) {
        rows = [true, false].map(truth => {
          const spellings = rows.filter(row => ChargingPointConfigurationValue.truth(row.value) === truth)
          return {id: 'truth-' + truth, value: String(truth), values: spellings.map(row => row.value), spellings, chargingPoints: spellings.reduce((sum, row) => sum + row.chargingPoints, 0), missing: false}
        }).filter(row => row.spellings.length)
      }
      const missing = this.data.read - this.data.withKey
      if (missing > 0) {
        rows.push({id: 'missing', value: null, chargingPoints: missing, missing: true})
      }
      return rows
    },
    shownRows() {
      const search = (this.valueSearch || '').trim().toLowerCase()
      return search ? this.rows.filter(row => row.missing || (row.values || [row.value]).some(value => String(value ?? '').toLowerCase().includes(search))) : this.rows
    },
    headers() {
      return [
        {text: this.$t('charging-point-fleet-configuration.charging-point'), value: 'name', sortable: false},
        {text: this.$t('charging-point-fleet-configuration.organization'), value: 'organizationName', sortable: false},
        {text: this.$t('charging-point-fleet-configuration.location'), value: 'locationName', sortable: false},
        {text: this.$t('classification.manufacturer') + ' · ' + this.$t('classification.product-line'), value: 'type', sortable: false},
        {text: this.$t('charging-point-fleet-configuration.since'), value: 'since', sortable: false},
      ]
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.load()
      }
    },
    keyName() {
      if (this.value) {
        this.load()
      }
    },
    listOptions: {
      handler() {
        this.loadList()
      },
      deep: true,
    },
  },

  mounted() {
    if (this.value) {
      this.load()
    }
  },

  methods: {
    load() {
      this.data = null
      this.selected = null
      this.list = null
      this.valueSearch = ''
      ChargingPointConfigurationRepository.search(this.filters, this.keyName).then(data => {
        this.data = data
      }).catch(err => {
        this.notifyError(err)
      })
    },

    select(row) {
      this.selected = row
      this.list = null
      this.listOptions = {...this.listOptions, page: 1}
    },

    loadList() {
      if (!this.selected) {
        return
      }
      const {page, itemsPerPage} = this.listOptions
      this.loadingList = true
      ChargingPointConfigurationRepository.chargingPoints(this.filters, this.keyName, {
        value: this.selected.value,
        values: this.selected.values || null,
        missing: this.selected.missing,
        offset: (page - 1) * itemsPerPage,
        limit: itemsPerPage,
      }).then(list => {
        this.list = list
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loadingList = false
      })
    },
  },
}
</script>

<style scoped lang="scss">
.mono {
  font-family: monospace;
}
.clickable {
  cursor: pointer;
}
.selected {
  background: rgba(0, 0, 0, 0.05);
}
</style>
